import { CSSProperties, useState } from 'react';

import { appBorders } from '@backrs_ui/common-ui/src/assets/appBorders';
import { appColors } from '@backrs_ui/common-ui/src/assets/appColors';
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { BackrsIcon } from '@backrs_ui/common-ui/src/atoms/BackrsIcon';

import { Box, Checkbox, FormControlLabel, Typography, Theme, SxProps } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';

import { TermsAndConditions } from './TermsAndConditions';
import { BackrsForm } from '../BackrsForm/BackrsForm';
import { BackrsFormField, BackrsFormValues } from '../BackrsForm/types';
import { RegistrationValues } from '../Login/login.util';

const REGISTER_STRINGS = appStrings.modules.register;

type RegisterProps = Readonly<{
  handleRegistration: (data: RegistrationValues) => void;
  errorMessage?: string;
  registerId?: string | null;
  preFilledData?: {
    first_name: string;
    last_name: string;
    email: string;
  } | null;
}>;

export const Register = ({
  handleRegistration,
  errorMessage,
  registerId,
  preFilledData,
}: RegisterProps) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const [isRegistering, setIsRegistering] = useState(false);

  const defaultRegistrationValues: BackrsFormValues = {
    first_name: preFilledData?.first_name ?? '',
    last_name: preFilledData?.last_name ?? '',
    phone: '',
    email: preFilledData?.email ?? '',
    password: '',
    invite_code: '',
    /** is_fourteen_plus will be converted from a string to a boolean when submitted to handleSubmission
     * @property {string} is_fourteen_plus
     * will be converted to
     * @property {boolean} is_fourteen_plus
     */
    is_fourteen_plus: '',
    ...(registerId && { register_id: registerId }),
  };

  const methods = useForm<BackrsFormValues>({
    defaultValues: defaultRegistrationValues,
  });

  const fields: BackrsFormField[] = [
    {
      id: 'first_name',
      name: 'first_name',
      placeholder: REGISTER_STRINGS.firstNamePlaceholder,
      type: 'text',
      autoComplete: 'firstName',
      autoFocus: true,
      rules: {
        required: REGISTER_STRINGS.error.firstNameRequired,
      },
    },
    {
      id: 'last_name',
      name: 'last_name',
      placeholder: REGISTER_STRINGS.lastNamePlaceholder,
      type: 'text',
      autoComplete: 'lastName',
      rules: {
        required: REGISTER_STRINGS.error.lastNameRequired,
      },
    },
    {
      id: 'phone',
      name: 'phone',
      placeholder: REGISTER_STRINGS.phonePlaceholder,
      type: 'tel',
      autoComplete: 'phone',
      rules: {
        required: REGISTER_STRINGS.error.phoneRequired,
        pattern: { value: /^\d{10}$/, message: REGISTER_STRINGS.error.invalidPhone },
      },
    },
    {
      id: 'email',
      name: 'email',
      placeholder: REGISTER_STRINGS.emailPlaceholder,
      type: 'text',
      autoComplete: 'email',
      rules: {
        required: REGISTER_STRINGS.error.emailRequired,
        pattern: { value: /^\S+@\S+$/i, message: REGISTER_STRINGS.error.invalidEmail },
      },
    },
    {
      id: 'password',
      name: 'password',
      placeholder: REGISTER_STRINGS.passwordPlaceholder,
      type: isPasswordVisible ? 'text' : 'password',
      autoComplete: 'current-password',
      icon: {
        type: 'RIGHT_ICON',
        rightIcon: 'Visibility',
        rightIconColor: appColors.neutral.lightGray,
        onRightIconClick: () => setIsPasswordVisible((prev) => !prev),
      },
      rules: { required: REGISTER_STRINGS.error.passwordRequired },
    },
    {
      id: 'invite_code',
      name: 'invite_code',
      placeholder: REGISTER_STRINGS.inviteCodePlaceholder,
      type: 'text',
    },
  ];

  const handleSubmission = (data: BackrsFormValues) => {
    setIsRegistering(true);

    const transformedData: RegistrationValues = {
      first_name: data.first_name as string,
      last_name: data.last_name as string,
      phone: data.phone as string,
      email: data.email as string,
      password: data.password as string,
      invite_code: data.invite_code ? (data.invite_code as string) : null,
      is_fourteen_plus: !!data.is_fourteen_plus,
      register_id: registerId ?? null,
    };

    handleRegistration(transformedData);
  };

  return (
    <Box sx={containerStyle}>
      <BackrsIcon
        name="BackrsWithText"
        alt={appStrings.modules.login.backrsLogoAlt}
        style={responsiveBackrsLogoStyle}
      />
      <Box sx={loginFormStyle}>
        <Typography variant="h4" sx={titleStyle}>
          {REGISTER_STRINGS.title}
        </Typography>
        <BackrsForm
          fields={fields}
          onSubmit={handleSubmission}
          buttonLabel={REGISTER_STRINGS.buttonLabel}
          methods={methods}
          isLoading={isRegistering}
        >
          <FormControlLabel
            label={REGISTER_STRINGS.ageCertification}
            sx={checkboxLabelStyle}
            control={
              <Controller
                key="is_fourteen_plus"
                name="is_fourteen_plus"
                control={methods.control}
                rules={{ required: '', validate: (value) => `${value}` === 'true' }} // adding this forces it to be checked
                render={({ field: controllerField }) => (
                  <Checkbox
                    {...controllerField}
                    checked={!!controllerField.value}
                    sx={checkboxStyle}
                  />
                )}
              />
            }
          />
        </BackrsForm>
        <TermsAndConditions />
        {errorMessage && (
          <Box mt={2} color={appColors.color.red1}>
            {errorMessage}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const containerStyle: SxProps<Theme> = {
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  px: 4,
} as const;

const titleStyle: SxProps<Theme> = {
  color: appColors.color.navy3,
  fontWeight: 700,
  mb: 3,
} as const;

const loginFormStyle: SxProps<Theme> = {
  backgroundColor: appColors.neutral.white,
  paddingX: 2,
  paddingY: 3,
  borderRadius: 4,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '100%',
  width: '100%',
  boxSizing: 'border-box',
  '@media (min-width: 300px)': {
    paddingX: 2,
    paddingY: 4,
  },
  '@media (min-width: 600px)': {
    maxWidth: '500px',
    paddingX: 11,
    paddingY: 8,
  },
  ...appBorders.backrsDropShadowBorders.black,
} as const;

const responsiveBackrsLogoStyle: CSSProperties = {
  width: '100%',
  maxWidth: '175px',
  padding: '8px',
  marginBottom: '32px',
} as const;

const checkboxLabelStyle: SxProps<Theme> = {
  '.MuiFormControlLabel-label': {
    color: appColors.color.navy2,
    fontSize: '0.75rem',
  },
} as const;

const checkboxStyle: SxProps<Theme> = {
  color: appColors.color.navy2,
  '&.Mui-checked': {
    color: appColors.color.navy2,
  },
} as const;
